import axiosClient from "../shared/api/axios-client";
import { CreatePackageInputs, Package, UpdatePackageInputs, CreatePromotionInput, UpdatePromotionInput, Promotion } from "./packages.interfaces";

/**
 * Busca pacotes com paginação
 * @param page Número da página (padrão: 1)
 * @param limit Limite de itens por página (padrão: 9)
 * @returns Lista de pacotes
 */
export const apiGetPackages = async (page: number = 1, limit: number = 9): Promise<Package[]> => {
    try {
        const response = await axiosClient.get<any[]>(`/packages/query?page=${page}&limit=${limit}`);
        // Mapear 'image' do backend para 'imageUrl' do frontend
        return response.data.map((pkg) => ({
            ...pkg,
            imageUrl: pkg.image || pkg.imageUrl || "",
        }));
    } catch (error) {
        console.error("Error during get packages:", error);
        throw error;
    }
};

/**
 * Busca um pacote específico pelo ID
 * @param id ID do pacote
 * @returns Detalhes do pacote
 */
export const apiGetPackage = async (id: string): Promise<Package> => {
    try {
        const response = await axiosClient.get<any>(`/packages/${id}`);
        // Mapear 'image' do backend para 'imageUrl' do frontend
        return {
            ...response.data,
            imageUrl: response.data.image || response.data.imageUrl || "",
        };
    } catch (error) {
        console.error("Error during get package:", error);
        throw error;
    }
};

/**
 * Cria um novo pacote
 * @param data Dados do pacote a ser criado
 */
export const apiCreatePackage = async (data: CreatePackageInputs): Promise<void> => {
    try {
        await axiosClient.post('/packages', data);
    } catch (error) {
        console.error("Error during create package:", error);
        throw error;
    }
};

/**
 * Atualiza um pacote existente
 * @param id ID do pacote a ser atualizado
 * @param data Dados de atualização do pacote
 */
export const apiUpdatePackage = async (id: string, data: UpdatePackageInputs): Promise<void> => {
    try {
        await axiosClient.put('/packages', { ...data, id });
    } catch (error) {
        console.error("Error during update package:", error);
        throw error;
    }
};

/**
 * Exclui pacotes
 * @param ids Lista de IDs dos pacotes a serem excluídos
 */
export const apiDeletePackage = async (ids: string[]): Promise<void> => {
    try {
        await axiosClient.delete('/packages', { data: ids });
    } catch (error) {
        console.error("Error during delete package:", error);
        throw error;
    }
};

/**
 * Busca todos os módulos de um pacote
 * @param packageId ID do pacote
 * @returns Lista de módulos do pacote
 */
export const apiGetPackageModules = async (packageId: string): Promise<any[]> => {
    try {
        const response = await axiosClient.get<any[]>(`/packages/${packageId}/modules`);
        return response.data;
    } catch (error) {
        console.error("Error during get package modules:", error);
        throw error;
    }
};

/**
 * Adiciona um módulo específico a um pacote
 * @param packageId ID do pacote
 * @param moduleId ID do módulo a ser adicionado
 * @returns Resultado da operação
 */
export const apiAddPackageModule = async (packageId: string, moduleId: string): Promise<any> => {
    try {
        const response = await axiosClient.post(`/packages/${packageId}/modules`, { moduleId });
        return response.data;
    } catch (error) {
        console.error("Error during add package module:", error);
        throw error;
    }
};

/**
 * Sincroniza módulos de um pacote
 * @param packageId ID do pacote
 * @param syncData Dados de sincronização dos módulos
 * @returns Resultado da sincronização
 */
export const apiSyncPackageModules = async (packageId: string, syncData: any): Promise<any> => {
    try {
        const response = await axiosClient.post(`/packages/${packageId}/modules/sync`, syncData);
        return response.data;
    } catch (error) {
        console.error("Error during sync package modules:", error);
        throw error;
    }
};

/**
 * Remove um módulo específico de um pacote
 * @param packageId ID do pacote
 * @param moduleId ID do módulo a ser removido
 */
export const apiDeletePackageModule = async (packageId: string, moduleId: string): Promise<void> => {
    try {
        await axiosClient.delete(`/packages/${packageId}/modules/${moduleId}`);
    } catch (error) {
        console.error("Error during delete package module:", error);
        throw error;
    }
};

/**
 * Adiciona múltiplos módulos a um pacote em uma única operação
 * @param packageId ID do pacote
 * @param moduleIds Lista de IDs dos módulos a serem adicionados
 * @returns Lista de módulos adicionados
 */
export const apiBatchAddPackageModules = async (packageId: string, moduleIds: string[]): Promise<any[]> => {
    try {
        const response = await axiosClient.post<any[]>(`/packages/${packageId}/modules/batch`, { moduleIds });
        return response.data;
    } catch (error) {
        console.error("Error during batch add package modules:", error);
        throw error;
    }
};

/**
 * Remove múltiplos módulos de um pacote em uma única operação
 * @param packageId ID do pacote
 * @param moduleIds Lista de IDs dos módulos a serem removidos
 */
export const apiBatchDeletePackageModules = async (packageId: string, moduleIds: string[]): Promise<void> => {
    try {
        await axiosClient.delete(`/packages/${packageId}/modules/batch`, { data: { moduleIds } });
    } catch (error) {
        console.error("Error during batch delete package modules:", error);
        throw error;
    }
};

/**
 * Cria uma nova promoção para um pacote
 * @param packageId ID do pacote
 * @param data Dados da promoção a ser criada
 * @returns ID da promoção criada
 */
export const apiCreatePromotion = async (packageId: string, data: CreatePromotionInput): Promise<string> => {
    try {
        const response = await axiosClient.post(`/packages/${packageId}/promotions`, data);
        return response.data.id;
    } catch (error) {
        console.error("Error during create promotion:", error);
        throw error;
    }
};

/**
 * Exclui uma promoção específica
 * @param packageId ID do pacote
 * @param promotionId ID da promoção a ser excluída
 */
export const apiDeletePromotion = async (packageId: string, promotionId: string): Promise<void> => {
    try {
        await axiosClient.delete(`/packages/${packageId}/promotions/${promotionId}`);
    } catch (error) {
        console.error("Error during delete promotion:", error);
        throw error;
    }
};

/**
 * Atualiza uma promoção existente
 * @param packageId ID do pacote
 * @param promotionId ID da promoção a ser atualizada
 * @param data Dados de atualização da promoção
 */
export const apiUpdatePromotion = async (packageId: string, promotionId: string, data: UpdatePromotionInput): Promise<void> => {
    try {
        await axiosClient.put(`/packages/${packageId}/promotions/${promotionId}`, data);
    } catch (error) {
        console.error("Error during update promotion:", error);
        throw error;
    }
};

/**
 * Busca todas as promoções de um pacote
 * @param packageId ID do pacote
 * @returns Lista de promoções do pacote
 */
export const apiGetPromotions = async (packageId: string): Promise<Promotion[]> => {
    try {
        const response = await axiosClient.get<Promotion[]>(`/packages/${packageId}/promotions`);
        return response.data;
    } catch (error) {
        console.error("Error during get promotions:", error);
        throw error;
    }
};

/**
 * Busca todos os módulos disponíveis no sistema que não estão no pacote atual
 * @param packageId ID do pacote atual (para filtrar módulos já incluídos)
 * @returns Lista de módulos disponíveis para adicionar
 */
export const apiGetAvailableModules = async (packageId?: string): Promise<any[]> => {
    try {
        // Buscar todos os módulos do sistema
        const allModulesResponse = await axiosClient.get<any[]>('/modules');
        const allModules = allModulesResponse.data;

        // Se não foi fornecido packageId, retornar todos os módulos
        if (!packageId) {
            return allModules;
        }

        // Buscar módulos já incluídos no pacote
        const packageModulesResponse = await axiosClient.get<any[]>(`/packages/${packageId}/modules`);
        const packageModules = packageModulesResponse.data;
        
        // Filtrar módulos que não estão no pacote atual
        const packageModuleIds = new Set(packageModules.map(module => module.id));
        const availableModules = allModules.filter(module => !packageModuleIds.has(module.id));
        
        return availableModules;
    } catch (error) {
        console.error("Error during get available modules:", error);
        throw error;
    }
}; 